import { useCallback, useEffect, useRef } from 'react';

import { useDownloadPit, usePitDataQuery } from '@/api/pit/getters';
import FolderIcon from '@/assets/folder.svg?react';
import PdfIcon from '@/assets/pdf.svg?react';
import Button from '@/components/ui/Button';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import * as Sentry from '@sentry/react';

export const PitDownloadCard = () => {
  const { i18n } = useLingui();
  const downloadLinkRef = useRef<HTMLAnchorElement>(null);
  const downloadTriedRef = useRef(false);

  const { data } = usePitDataQuery();
  const { mutateAsync: downloadPit } = useDownloadPit();
  const fileUrl = data?.url;
  const filename = data?.filename;

  const handleDownloadBlob = useCallback(async () => {
    if (!fileUrl) return;

    try {
      const blob = await downloadPit(fileUrl);
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(pdfBlob);

      if (downloadLinkRef.current) {
        downloadLinkRef.current.href = blobUrl;
        downloadLinkRef.current.download = filename ?? 'PIT-11.pdf';
        downloadLinkRef.current.click();

        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          if (downloadLinkRef.current) {
            downloadLinkRef.current.href = '';
          }
        }, 100);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [downloadPit, fileUrl, filename]);

  useEffect(() => {
    if (fileUrl && !downloadTriedRef.current) {
      downloadTriedRef.current = true;
      handleDownloadBlob();
    }
  }, [fileUrl, handleDownloadBlob]);

  const handleDownload = () => {
    handleDownloadBlob();
  };

  return (
    <div
      className={cn(
        'mx-auto w-full space-y-4 rounded-[10px] bg-white px-4 py-6 shadow-sm md:space-y-6 md:rounded-lg md:p-8'
      )}
    >
      <div className="flex items-center gap-4">
        <div className="flex size-12 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
          <FolderIcon className="size-6" />
        </div>
        <h2 className="font-semibold text-blue-600">
          {i18n.t('Za chwilę Twój PIT-11 zostanie pobrany')}
        </h2>
      </div>

      <div className="space-y-4 text-sm md:text-base">
        <p>
          {i18n.t(
            'Przypominamy, że w związku z zawarciem umowy cesji wierzytelności dot. szkody na pojeździe, szkody na nieruchomości lub sankcji kredytu darmowego z Helpfind, masz do odebrania'
          )}{' '}
          <span className="font-semibold">
            {i18n.t('PIT-11 za poprzedni rok')}
          </span>
          .
        </p>

        <p>
          {i18n.t(
            'W Polsce tego typu usługa jest objęta podatkiem 12% od osób fizycznych.'
          )}
        </p>

        <div className="rounded-lg bg-gray-50 p-4">
          <h4 className="mb-2 font-semibold">
            {i18n.t('Koniecznie przeczytaj poniższe informacje!')}
          </h4>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              {i18n.t('Zgodnie z zapisem w umowie,')}{' '}
              <span className="font-semibold">
                {i18n.t('podatek został już naliczony i odprowadzony')}
              </span>{' '}
              {i18n.t('do urzędu skarbowego – niczego nie musisz płacić.')}
            </li>
            <li>
              {i18n.t('Kwoty z PIT-11')}{' '}
              <span className="font-semibold">
                {i18n.t('należy uwzględnić')}
              </span>{' '}
              {i18n.t('w swoim rozliczeniu rocznym.')}
            </li>
            <li>
              {i18n.t(
                'Jeśli w międzyczasie zmienił się Twój adres zamieszkania, ale'
              )}{' '}
              <span className="font-semibold">{i18n.t('nie zmienił')}</span>{' '}
              {i18n.t('się urząd skarbowy, wówczas PIT-11 nie wymaga korekty.')}
            </li>
            <li>
              {i18n.t(
                'W celu otwarcia załącznika należy pobrać program Adobe Acrobat Reader oraz otworzyć PIT-11 korzystając z tego programu.'
              )}{' '}
              <span className="font-semibold">{i18n.t('Hasłem')}</span>{' '}
              {i18n.t('do otwarcia pliku PDF, są')}{' '}
              <span className="font-semibold">
                {i18n.t('4 ostatnie cyfry Twojego numeru PESEL')}
              </span>
              .
            </li>
          </ul>
        </div>
        <p>
          {i18n.t('Sprawdź czy wszystkie dane się zgadzają.')}{' '}
          {i18n.t(
            'Sprawdź, czy wszystkie dane się zgadzają. Jeśli pobieranie nie rozpoczęło się automatycznie, kliknij w poniższy przycisk.'
          )}{' '}
          <p>{i18n.t('Dziękujemy za skorzystanie z usług Helpfind!')}</p>
        </p>
      </div>
      <a
        ref={downloadLinkRef}
        className="hidden"
        rel="noopener noreferrer"
        aria-label={i18n.t('Pobierz PIT-11')}
      />
      <Button onClick={handleDownload} className="w-full  lg:w-full">
        <div className="flex w-full items-center gap-2">
          {i18n.t('Pobierz PIT-11')} <PdfIcon className="size-5 text-white" />
        </div>
      </Button>
    </div>
  );
};
